import { FC, useMemo, useState } from "react";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AssignmentIcon from "@mui/icons-material/Assignment";
import UpdateIcon from "@mui/icons-material/Update";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Link,
	List,
	ListItem,
	Skeleton,
	Typography,
} from "@mui/material";
import dayjs from "dayjs";

import { useTime } from "@/components/Application/TimeContext";
import { Translate } from "@/components/Application/Translate";
import { ArchiveProcedureLink } from "@/components/Archive/Pages/ArchiveProcedure/Components/ArchiveProcedureLink";
import WaterfallChart from "@/components/Archive/WaterfallChart";

import { useFetchArchivedProcedures } from "@/service/ArchiveQueries";

export interface IActiveArchiveProcedures {
	startDate?: Date;
	endDate?: Date;
	selectedUser?: number;
	expanded?: boolean;
}

export const ActiveArchiveProcedures: FC<IActiveArchiveProcedures> = (
	props,
) => {
	const options = {
		StartDate: props.startDate ? dayjs(props.startDate).toDate() : undefined,
		EndDate: props.endDate
			? dayjs(props.endDate).startOf("day").add(1, "day").toDate()
			: undefined,
		SelectedUser: props.selectedUser ?? 0,
	};

	const { isLoading, data: archivedProcedures } =
		useFetchArchivedProcedures(options);
	const { timezone } = useTime();

	const [open, setOpen] = useState(false);
	const [selectedItem, setSelectedItem] = useState<any>(null);

	const handleClickOpen = (item: any) => {
		setSelectedItem(item);
		setOpen(true);
	};

	const handleClose = () => {
		setOpen(false);
		setSelectedItem(null);
	};

	const filteredProcedures = useMemo(() => {
		if (props.selectedUser === 0) {
			return archivedProcedures?.ArchivedProjects;
		}

		return archivedProcedures?.ArchivedProjects.filter((proc) =>
			proc.Users.some((user) => user.UserId === props.selectedUser),
		);
	}, [archivedProcedures, props.selectedUser]);
	if (isLoading) {
		return (
			<>
				<Skeleton variant="rounded" width={"100%"} height={60} />
				<br />
				<Skeleton variant="rounded" width={"100%"} height={60} />
			</>
		);
	}

	// TODO make it scrollable and fix mobile
	return (
		<Box
			sx={{
				mt: 2,
				padding: 3,
				backgroundColor: "#f2f2f2",
				borderRadius: "6px",
			}}
		>
			<Typography variant="h5" sx={{ marginBottom: 2 }}>
				<Translate String="Archive.History.Header">History</Translate>
			</Typography>
			<List>
				{filteredProcedures?.map((item) => (
					<ListItem
						key={item.Id}
						sx={{
							display: "flex",
							flexDirection: "column",
							padding: 2,
							borderBottom: "1px solid #e0e0e0",
							"&:last-child": { borderBottom: "none" },
							backgroundColor: "white",
							mb: 2,
							cursor: "pointer",
							alignItems: "stretch",
						}}
						onClick={() => handleClickOpen(item)}
					>
						<Grid container spacing={2}>
							<Grid item xs={8}>
								<Typography variant="h6" sx={{ marginBottom: 1 }}>
									{item.Name}
								</Typography>
								<Typography
									variant="body2"
									color="textSecondary"
									sx={{ marginBottom: 1 }}
								>
									{item.Description}
								</Typography>
								<Typography
									variant="body2"
									color="textSecondary"
									sx={{ marginBottom: 1 }}
								>
									{dayjs
										.utc(item.Created)
										.tz(timezone)
										.format("DD/MM/YY  HH:mm:ss")}
								</Typography>

								{/* Display Users */}
								{item.Users && item.Users.length > 0 && (
									<Box sx={{ marginTop: 1 }}>
										{item.Users.map((user: any) => (
											<Typography
												key={user.UserId}
												variant="body2"
												color="textSecondary"
												sx={{ marginBottom: 0.5 }}
											>
												{user.UserFullName}
												{props.expanded && " - "}
												{props.expanded && (
													<Link
														href={`mailto:${user.UserEmail}`}
														onClick={(e) => e.stopPropagation()}
														underline="hover"
													>
														{user.UserEmail}
													</Link>
												)}
											</Typography>
										))}
									</Box>
								)}
							</Grid>
							<Grid
								item
								xs={4}
								container
								direction="column"
								alignItems="flex-end"
							>
								{item.Updates.length > 1 && (
									<Grid container alignItems="center" justifyContent="flex-end">
										<Grid item sx={{ display: "flex", alignItems: "center" }}>
											<AccessTimeIcon
												sx={{
													fontSize: "1rem",
													color: "#555",
													marginRight: 0.5,
												}}
											/>
											<Typography
												variant="body2"
												sx={{ color: "#555", fontWeight: "550" }}
											>
												Total Duration:
											</Typography>
										</Grid>
										<Grid item>
											<Typography
												variant="body2"
												sx={{ color: "#333", marginLeft: 1 }}
											>
												{calculateDuration(item.Updates)}
											</Typography>
										</Grid>
									</Grid>
								)}
								<Box sx={{ marginTop: 1 }}>
									<ArchiveProcedureLink item={item} />
								</Box>
							</Grid>
						</Grid>
					</ListItem>
				))}
			</List>

			{/* Dialog for displaying item details */}
			{selectedItem && (
				<Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
					<DialogTitle>{selectedItem.Name}</DialogTitle>
					<DialogContent>
						{/* Main info  */}
						<Box
							sx={{
								marginBottom: 3,
								padding: 2,
								backgroundColor: "#f0f4f8",
								borderRadius: "8px",
								display: "flex",
								flexDirection: "row",
								gap: 2, // Spacing between columns
								justifyContent: "space-between",
							}}
						>
							{/* Left Column */}
							<Box sx={{ flexBasis: "48%" }}>
								<Typography
									variant="body1"
									sx={{ fontWeight: "bold", fontSize: "1rem" }}
								>
									Description:
								</Typography>
								<Typography
									variant="body2"
									color="textSecondary"
									sx={{ marginBottom: 1 }}
								>
									{selectedItem.Description}
								</Typography>
								<Grid container alignItems="center" sx={{ marginBottom: 1 }}>
									<Grid item sx={{ display: "flex", alignItems: "center" }}>
										<AccessTimeIcon
											sx={{ fontSize: "1rem", color: "#555", marginRight: 0.5 }}
										/>
										<Typography
											variant="body2"
											sx={{ color: "#555", fontWeight: "550" }}
										>
											Created:
										</Typography>
									</Grid>
									<Grid item>
										<Typography
											variant="body2"
											sx={{ color: "#333", marginLeft: 1 }}
										>
											{dayjs
												.utc(selectedItem.Created)
												.tz(timezone)
												.format("DD/MM/YY HH:mm:ss")}
										</Typography>
									</Grid>
								</Grid>
								<Grid container alignItems="center">
									<Grid item sx={{ display: "flex", alignItems: "center" }}>
										<UpdateIcon
											sx={{ fontSize: "1rem", color: "#555", marginRight: 0.5 }}
										/>
										<Typography
											variant="body2"
											sx={{ color: "#555", fontWeight: "550" }}
										>
											Updated:
										</Typography>
									</Grid>
									<Grid item>
										<Typography
											variant="body2"
											sx={{ color: "#333", marginLeft: 1 }}
										>
											{dayjs
												.utc(selectedItem.Updated)
												.tz(timezone)
												.format("DD/MM/YY HH:mm:ss")}
										</Typography>
									</Grid>
								</Grid>
							</Box>

							{/* Right Column */}
							<Box sx={{ flexBasis: "48%", mt: 4 }}>
								<Grid container alignItems="center" sx={{ marginBottom: 1 }}>
									<Grid item sx={{ display: "flex", alignItems: "center" }}>
										<AssignmentIcon
											sx={{ fontSize: "1rem", color: "#555", marginRight: 0.5 }}
										/>
										<Typography
											variant="body2"
											sx={{ color: "#555", fontWeight: "550" }}
										>
											Project Type:
										</Typography>
									</Grid>
									<Grid item>
										<Typography
											variant="body2"
											sx={{ color: "#333", marginLeft: 1 }}
										>
											{selectedItem.ProjectType}
										</Typography>
									</Grid>
								</Grid>
								{selectedItem.Updates.length > 1 && (
									<Grid container alignItems="center" sx={{ marginTop: 1 }}>
										<Grid item sx={{ display: "flex", alignItems: "center" }}>
											<AccessTimeIcon
												sx={{
													fontSize: "1rem",
													color: "#555",
													marginRight: 0.5,
												}}
											/>
											<Typography
												variant="body2"
												sx={{ color: "#555", fontWeight: "550" }}
											>
												Total Duration:
											</Typography>
										</Grid>
										<Grid item>
											<Typography
												variant="body2"
												sx={{ color: "#333", marginLeft: 1 }}
											>
												{calculateDuration(selectedItem.Updates)}
											</Typography>
										</Grid>
									</Grid>
								)}
							</Box>
						</Box>
						<Box sx={{ m: 2 }}>
							<WaterfallChart updates={selectedItem.Updates} />
						</Box>
						{/* Updates */}
						<Box>
							<Typography variant="h6" sx={{ marginBottom: 2 }}>
								Updates
							</Typography>
							<List>
								{selectedItem.Updates.map((update: any, index: number) => (
									<ListItem
										key={index}
										sx={{
											display: "flex",
											flexDirection: "row",
											justifyContent: "space-between",
											alignItems: "center",
											padding: 2,
											backgroundColor: "#f0f4f8",
											marginBottom: 1,
											borderRadius: "4px",
										}}
									>
										{/* Status Indicator */}
										<Box sx={{ display: "flex", alignItems: "center" }}>
											<Box
												sx={{
													width: 16,
													height: 16,
													borderRadius: "50%",
													backgroundColor:
														update.Status === 1 ? "green" : "red",
													marginRight: 2,
												}}
											/>
											<Typography variant="body2" sx={{ fontWeight: "550" }}>
												{update.User.UserFullName}
											</Typography>
										</Box>

										{/* Update Description */}
										<Box sx={{ flex: 1, marginLeft: 2 }}>
											<Typography variant="body2">
												{update.Status === 1 ? "Active" : "Inactive"}
											</Typography>
										</Box>

										{/* Start Time */}
										<Box
											sx={{
												display: "flex",
												flexDirection: "column",
												alignItems: "flex-end",
											}}
										>
											<Typography variant="body2" sx={{ textAlign: "right" }}>
												Started at{" "}
												{dayjs
													.utc(update.Date)
													.tz(timezone)
													.format("DD/MM/YY HH:mm:ss")}
											</Typography>
											{index > 0 && (
												<Typography
													variant="body2"
													sx={{ fontStyle: "italic", textAlign: "right" }}
												>
													{calculateUpdateDuration(
														selectedItem.Updates[index - 1].Date,
														update.Date,
													)}
												</Typography>
											)}
										</Box>
									</ListItem>
								))}
							</List>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleClose} color="primary">
							Close
						</Button>
					</DialogActions>
				</Dialog>
			)}
		</Box>
	);
};

const calculateDuration = (updates: any[]) => {
	if (updates.length < 2) return "N/A"; // Not enough updates to calculate duration

	// Sort updates by date to ensure the first and last are correctly identified
	const sortedUpdates = updates.sort(
		(a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime(),
	);

	// Get the first and last update dates
	const startDate = dayjs(sortedUpdates[0].Date);
	const endDate = dayjs(sortedUpdates[sortedUpdates.length - 1].Date);

	// Calculate the duration
	const days = endDate.diff(startDate, "day");
	const hours = endDate.diff(startDate, "hour") % 24;
	const minutes = endDate.diff(startDate, "minute") % 60;

	// Format the duration as a string
	let formattedDuration = "";
	if (days > 0) formattedDuration += `${days} day${days > 1 ? "s" : ""} `;
	if (hours > 0) formattedDuration += `${hours} hour${hours > 1 ? "s" : ""} `;
	if (minutes > 0)
		formattedDuration += `${minutes} minute${minutes > 1 ? "s" : ""} `;

	return formattedDuration.trim() || "Less than a minute";
};

export const calculateUpdateDuration = (
	previousDate: string,
	currentDate: string,
) => {
	const start = dayjs(previousDate);
	const end = dayjs(currentDate);

	const days = end.diff(start, "day");
	const hours = end.diff(start, "hour") % 24;
	const minutes = end.diff(start, "minute") % 60;

	let durationString = "";
	if (days > 0) durationString += `${days} day${days > 1 ? "s" : ""} `;
	if (hours > 0) durationString += `${hours} hour${hours > 1 ? "s" : ""} `;
	if (minutes > 0)
		durationString += `${minutes} minute${minutes > 1 ? "s" : ""} `;

	return durationString.trim() || "Less than a minute";
};
